import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import {
  FaSignInAlt,
  FaSearch,
  FaThumbsUp,
  FaThumbsDown,
  FaSyncAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaKey,
  FaTrash,
} from "react-icons/fa";

const BASE_URL = "https://cam4k.vn/api"; // đổi nếu cần

async function api(path, { method = "GET", body, token } = {}) {
  const url =
    method === "GET" && body
      ? `${BASE_URL}${path}?${new URLSearchParams(body)}`
      : `${BASE_URL}${path}`;
  const res = await fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: method !== "GET" && body ? JSON.stringify(body) : undefined,
  });
  const text = await res.text().catch(() => "");
  if (!res.ok) {
    let err = text;
    try {
      const j = JSON.parse(text);
      err = j.error || j.message || text || res.statusText;
    } catch (_) {}
    throw new Error(`${res.status} ${err}`.trim());
  }
  const ct = res.headers.get("content-type") || "";
  return ct.includes("application/json") ? JSON.parse(text) : text;
}

function Toast({ kind = "info", children }) {
  const cls =
    kind === "error"
      ? "bg-red-50 text-red-700 border-red-200"
      : kind === "success"
      ? "bg-green-50 text-green-700 border-green-200"
      : "bg-gray-50 text-gray-700 border-gray-200";
  const Icon =
    kind === "error"
      ? FaTimesCircle
      : kind === "success"
      ? FaCheckCircle
      : FaInfo;
  return (
    <div
      className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${cls}`}
    >
      <Icon />
      <span className="text-sm">{children}</span>
    </div>
  );
}
function FaInfo(props) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" {...props}>
      <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.1" />
      <path d="M11 10h2v7h-2zM11 7h2v2h-2z" fill="currentColor" />
    </svg>
  );
}

export default function AdminAccessConsole() {
  // auth state (JWT)
  const [username, setUsername] = useState(
    () => localStorage.getItem("admin_username") || ""
  );
  const [password, setPassword] = useState("");
  const [token, setToken] = useState(
    () => localStorage.getItem("admin_token") || ""
  );
  const [role, setRole] = useState(
    () => localStorage.getItem("admin_role") || ""
  );
  const isAuthed = !!token && role === "admin";

  // approver mặc định = username đã đăng nhập
  const approver = username || "admin";

  // data state
  const [statusFilter, setStatusFilter] = useState("pending");
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [error, setError] = useState("");

  // paging (client-side cho bảng đã tải)
  const [page, setPage] = useState(1);
  const pageSize = 25;

  // toast
  const [toastMsg, setToastMsg] = useState(null);
  const [toastKind, setToastKind] = useState("info");
  const showToast = (k, m) => {
    setToastKind(k);
    setToastMsg(m);
    setTimeout(() => setToastMsg(null), 2500);
  };

  const filtered = useMemo(() => {
    const text = q.trim().toLowerCase();
    return requests.filter((r) => {
      if (!text) return true;
      return (
        (r.username || "").toLowerCase().includes(text) ||
        (r.device_fingerprint || "").toLowerCase().includes(text) ||
        (r.note || "").toLowerCase().includes(text)
      );
    });
  }, [requests, q]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page]);

  // auth
  async function doLogin() {
    try {
      if (!username || !password) {
        setError("Nhập username và password");
        return;
      }
      setError("");
      const res = await api("/auth/login", {
        method: "POST",
        body: { username, password },
      });
      if (!res.ok) throw new Error(res.error || "Đăng nhập thất bại");
      if (res.role !== "admin") {
        throw new Error("Tài khoản không có quyền admin");
      }
      setToken(res.token);
      setRole(res.role);
      localStorage.setItem("admin_token", res.token);
      localStorage.setItem("admin_role", res.role);
      localStorage.setItem("admin_username", username);
      setPassword("");
      await load(statusFilter, res.token);
    } catch (e) {
      setError(e.message || "Đăng nhập thất bại");
    }
  }

  function logout() {
    setToken("");
    setRole("");
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_role");
  }

  // load requests từ API (server hỗ trợ lọc & phân trang; ở đây demo lấy tất theo filter status)
  async function load(status = statusFilter, tkn = token) {
    if (!tkn) return;
    setLoading(true);
    setError("");
    try {
      const res = await api("/admin/access/requests", {
        method: "GET",
        body: { status },
        token: tkn,
      });
      // backend trả { total, page, page_size, items }
      const items = Array.isArray(res) ? res : res.items || [];
      setRequests(items);
      setPage(1);
    } catch (e) {
      setError(e.message);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (isAuthed) load(); // eslint-disable-next-line
  }, [statusFilter, isAuthed]);

  // actions
  async function approve(r, days = 90) {
    try {
      await api("/access/approve", {
        method: "POST",
        body: {
          username: r.username,
          device_fingerprint: r.device_fingerprint,
          approver_username: approver,
          days_valid: days,
        },
        token,
      });
      showToast("success", `Đã duyệt ${r.username}`);
      load();
    } catch (e) {
      showToast("error", e.message);
    }
  }

  async function deny(r, note = "") {
    try {
      await api("/access/deny", {
        method: "POST",
        body: {
          username: r.username,
          device_fingerprint: r.device_fingerprint,
          approver_username: approver,
          note,
        },
        token,
      });
      showToast("success", `Đã từ chối ${r.username}`);
      load();
    } catch (e) {
      showToast("error", e.message);
    }
  }

  async function revokeAll(r) {
    try {
      await api("/signatures/revoke", {
        method: "POST",
        body: {
          username: r.username,
          device_fingerprint: r.device_fingerprint,
        },
        token,
      });
      showToast("success", `Đã revoke chữ ký của ${r.username}`);
    } catch (e) {
      showToast("error", e.message);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <header className="sticky top-0 z-40 bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <h1 className="text-lg font-semibold">Admin Access Console</h1>
          <span className="ml-auto" />
          {isAuthed ? (
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border bg-white hover:bg-gray-50"
              title="Đăng xuất"
            >
              <FaKey /> Đăng xuất
            </button>
          ) : null}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-4">
        {!isAuthed ? (
          <div className="max-w-xl mx-auto bg-white border rounded-2xl shadow-sm p-5">
            <h2 className="text-base font-semibold mb-3">Đăng nhập quản trị</h2>
            {error && <div className="mb-3 text-sm text-red-600">{error}</div>}
            <div className="grid gap-3">
              <label className="text-sm">
                <div className="mb-1">Username</div>
                <input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full border rounded-xl px-3 py-2"
                  placeholder="vd: admin"
                />
              </label>
              <label className="text-sm">
                <div className="mb-1">Password</div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border rounded-xl px-3 py-2"
                  placeholder="mật khẩu"
                />
              </label>
              <button
                onClick={doLogin}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-black text-white"
              >
                <FaSignInAlt /> Đăng nhập
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-3 flex flex-col md:flex-row md:items-center gap-3">
              <div className="flex items-center gap-2 bg-white border rounded-xl px-3 py-2 w-full md:w-auto">
                <FaSearch className="opacity-60" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Tìm username / fingerprint / note"
                  className="flex-1 outline-none text-sm"
                />
              </div>
              <div className="inline-flex items-center gap-2">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="border rounded-xl px-3 py-2 bg-white"
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="denied">Denied</option>
                  <option value="revoked">Revoked</option>
                  <option value="all">Tất cả</option>
                </select>
                <button
                  onClick={() => load()}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border bg-white hover:bg-gray-50"
                >
                  <FaSyncAlt /> Làm mới
                </button>
              </div>
              <div className="ml-auto text-sm text-gray-600">
                Đang đăng nhập: <b>{username}</b> ({role})
              </div>
            </div>

            {toastMsg && (
              <div className="mb-3">
                <Toast kind={toastKind}>{toastMsg}</Toast>
              </div>
            )}
            {loading && (
              <div className="mb-3 text-sm text-gray-600">
                Đang tải dữ liệu…
              </div>
            )}
            {error && <div className="mb-3 text-sm text-red-600">{error}</div>}

            <div className="bg-white border rounded-2xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-100 border-b">
                    <tr>
                      <th className="px-3 py-2 text-left sticky left-0 bg-gray-100 z-10">
                        User
                      </th>
                      <th className="px-3 py-2 text-left">Requested</th>
                      <th className="px-3 py-2 text-left">Approved</th>
                      <th className="px-3 py-2 text-left">Expires</th>
                      <th className="px-3 py-2 text-left">Status</th>
                      <th className="px-3 py-2 text-left sticky right-0 bg-gray-100 z-10">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageData.length === 0 ? (
                      <tr>
                        <td
                          colSpan={8}
                          className="px-4 py-8 text-center text-gray-500"
                        >
                          Không có dữ liệu
                        </td>
                      </tr>
                    ) : (
                      pageData.map((r) => (
                        <tr
                          key={`${r.username}-${r.device_fingerprint}-${
                            r.requested_at || Math.random()
                          }`}
                          className="border-b last:border-0 hover:bg-gray-50/60"
                        >
                          <td className="px-3 py-2 whitespace-nowrap sticky left-0 bg-white z-10">
                            {r.username}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {dayjs(r.requested_at).format("HH:mm DD/MM/YYYY") ||
                              "-"}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {dayjs(r.approved_at).format("HH:mm DD/MM/YYYY") ||
                              "-"}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {dayjs(r.expires_at).format("HH:mm DD/MM/YYYY") ||
                              "-"}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap capitalize">
                            {r.status}
                          </td>
                          <td className="px-3 py-2 sticky right-0 bg-white z-10">
                            <div className="flex items-center gap-2 justify-end">
                              <button
                                onClick={() => approve(r, 90)}
                                className="px-2 py-1 rounded-lg border bg-white hover:bg-gray-50"
                                title="Duyệt (90 ngày)"
                              >
                                <FaThumbsUp />
                              </button>
                              <button
                                onClick={() => deny(r)}
                                className="px-2 py-1 rounded-lg border bg-white hover:bg-gray-50"
                                title="Từ chối"
                              >
                                <FaThumbsDown />
                              </button>
                              <button
                                onClick={() => revokeAll(r)}
                                className="px-2 py-1 rounded-lg border bg-white hover:bg-gray-50"
                                title="Revoke tất cả chữ ký"
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between px-4 py-3">
                <div className="text-xs text-gray-500">
                  {filtered.length > 0 && (
                    <>
                      Hiển thị {(page - 1) * pageSize + 1}-
                      {Math.min(page * pageSize, filtered.length)} /{" "}
                      {filtered.length} yêu cầu
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1.5 rounded-lg border disabled:opacity-50"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                  >
                    Trước
                  </button>
                  <span className="text-sm">
                    Trang {page}/{totalPages}
                  </span>
                  <button
                    className="px-3 py-1.5 rounded-lg border disabled:opacity-50"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >
                    Sau
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </main>

      <footer className="max-w-6xl mx-auto px-4 py-6 text-xs text-gray-500">
        © {new Date().getFullYear()} Cam4K Software.
      </footer>
    </div>
  );
}
